import { FC, useMemo } from 'react';
import {
    Table,
    Tag,
    Tooltip,
    Space,
    Typography,
    Alert,
} from 'antd';
import { BulkColumnConfig, TranslateFn, ValidatedRow } from './types';

const { Text } = Typography;

interface PreviewStepProps {
    data: ValidatedRow[];
    columns: BulkColumnConfig[];
    t: TranslateFn;
}

const PreviewStep: FC<PreviewStepProps> = ({ data, columns, t }) => {
    const validCount = data.filter((d) => d._isValid).length;
    const invalidCount = data.filter((d) => !d._isValid).length;

    const getFieldErrors = (row: ValidatedRow, field: string): string[] => row._errors
        .filter((e) => e.field === field)
        .map((e) => t(e.message));

    const renderCell = (value: any, row: ValidatedRow, field: string) => {
        const errors = getFieldErrors(row, field);
        if (errors.length > 0) {
            return (
                <Tooltip title={errors.join(', ')}>
                    <Text type="danger" style={{ borderBottom: '1px dashed red' }}>
                        {value || '-'}
                    </Text>
                </Tooltip>
            );
        }
        return value || '-';
    };

    const tableColumns = useMemo(() => [
        {
            title: '#',
            dataIndex: '_rowIndex',
            key: '_rowIndex',
            width: 50,
            fixed: 'left' as const,
        },
        {
            title: t('BULK.COL_STATUS'),
            key: '_isValid',
            width: 80,
            fixed: 'left' as const,
            render: (_: any, record: ValidatedRow) => (
                record._isValid
                    ? <Tag color="success">{t('BULK.VALID')}</Tag>
                    : <Tag color="error">{t('BULK.INVALID')}</Tag>
            ),
        },
        ...columns.map((col) => ({
            title: col.label,
            dataIndex: col.key,
            key: col.key,
            width: col.width || 120,
            render: (v: any, r: ValidatedRow) => renderCell(v, r, col.key),
        })),
    ], [columns, t]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space size={16}>
                <Text strong>
                    {t('BULK.TOTAL_ROWS', { count: data.length })}
                </Text>
                <Tag color="success">{t('BULK.VALID_COUNT', { count: validCount })}</Tag>
                {invalidCount > 0 && (
                    <Tag color="error">{t('BULK.INVALID_COUNT', { count: invalidCount })}</Tag>
                )}
            </Space>

            {invalidCount > 0 && (
                <Alert
                    message={t('BULK.HAS_ERRORS')}
                    description={t('BULK.HAS_ERRORS_DESCRIPTION')}
                    type="warning"
                    showIcon
                />
            )}

            <Table
                dataSource={data}
                columns={tableColumns}
                rowKey="_rowIndex"
                size="small"
                scroll={{ x: 1500 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} ${t('BULK.RECORDS')}`,
                }}
                rowClassName={(record) => (!record._isValid ? 'ant-table-row-error' : '')}
            />
        </Space>
    );
};

export { PreviewStep };
